import { useEffect, useRef, useState } from "react";
import { ArrowRight, ArrowUp, Loader2, WandSparkles } from "lucide-react";
import { sendOfficeAssistantMessage } from "@nevisign/reader-core";
import { useAuth } from "../contexts/auth-context";
import {
  getDadnoThread,
  newDadnoThreadId,
  upsertDadnoThread,
  type DadnoChatMessage,
} from "../lib/dadno-chat-store";
import { ensureFreshAccessToken } from "../lib/platform-auth";

type Props = {
  apiBase: string;
  seedMessage: string;
  attachmentName?: string;
  documentText?: string;
  threadId?: string | null;
  mode?: "summary" | "risk" | "consult" | null;
  onBack: () => void;
};

/** Titles mirror platform ContractChat header density (`دستیار ${docLabel}`). */
const MODE_COPY: Record<
  "summary" | "risk" | "consult",
  { title: string; welcome: string; placeholder: string }
> = {
  summary: {
    title: "خلاصه اسناد حقوقی",
    welcome: "سلام. سند یا سؤال خود را بفرستید تا خلاصهٔ حقوقی دریافت کنید.",
    placeholder: "پیام بنویسید…",
  },
  risk: {
    title: "تحلیل ریسک قراردادها",
    welcome: "سلام. متن قرارداد یا نگرانی حقوقی را بفرستید تا ریسک‌ها را بررسی کنیم.",
    placeholder: "پیام بنویسید…",
  },
  consult: {
    title: "مشاوره هوشمند",
    welcome: "سلام. هر سؤال حقوقی دارید همین‌جا بپرسید.",
    placeholder: "سؤال یا موضوع مشاوره را بنویسید…",
  },
};

function mid(): string {
  return `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function welcomeMessage(mode: Props["mode"]): DadnoChatMessage {
  return {
    id: "welcome",
    role: "assistant",
    content: mode
      ? MODE_COPY[mode].welcome
      : "سلام. هر سؤال یا درخواستی دارید همین‌جا بپرسید.",
    createdAt: new Date().toISOString(),
  };
}

export function DadnoChatPage({
  apiBase,
  seedMessage,
  attachmentName,
  documentText,
  threadId: incomingThreadId,
  mode = null,
  onBack,
}: Props) {
  const { isAuthenticated, openLogin, requireAuth } = useAuth();
  const [threadId] = useState(() => incomingThreadId ?? newDadnoThreadId());
  const [messages, setMessages] = useState<DadnoChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seeded = useRef(false);
  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const title = mode ? MODE_COPY[mode].title : "دستیار دادنو";
  const placeholder = mode
    ? MODE_COPY[mode].placeholder
    : "پیام بنویسید…";

  useEffect(() => {
    const existing = getDadnoThread(threadId);
    if (existing?.messages?.length) {
      setMessages(existing.messages);
      seeded.current = true;
      return;
    }
    if (seeded.current) return;

    const seed = seedMessage.trim();
    if (!seed) {
      seeded.current = true;
      setMessages([welcomeMessage(mode)]);
      return;
    }

    seeded.current = true;
    const userMsg: DadnoChatMessage = {
      id: mid(),
      role: "user",
      content: attachmentName ? `${seed}\n\n[پیوست: ${attachmentName}]` : seed,
      createdAt: new Date().toISOString(),
    };
    const loadingMsg: DadnoChatMessage = {
      id: mid(),
      role: "assistant",
      content: "در حال اعمال…",
      createdAt: new Date().toISOString(),
      loading: true,
    };
    setMessages([userMsg, loadingMsg]);
    void sendToAssistant(userMsg.content, [userMsg], loadingMsg.id);
  }, [seedMessage, attachmentName, threadId, mode]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  function persist(next: DadnoChatMessage[]) {
    const preview = next.find((m) => m.role === "user")?.content ?? "گفتگو";
    upsertDadnoThread({
      id: threadId,
      title: preview.slice(0, 48) || "گفتگوی دادنو",
      preview: preview.slice(0, 120),
      updatedAt: new Date().toISOString(),
      messages: next.filter((m) => !m.loading),
      attachmentName,
    });
  }

  async function sendToAssistant(
    message: string,
    prior: DadnoChatMessage[],
    loadingId: string,
  ) {
    if (!isAuthenticated) {
      openLogin(() => void sendToAssistant(message, prior, loadingId));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ensureFreshAccessToken();
      const recent = prior
        .filter((m) => !m.loading && m.id !== "welcome")
        .slice(-8)
        .map((m) => ({ role: m.role, content: m.content }));

      // هاب دادنو باید همیشه مدل حقوقی (دادنو) را بزند — نه دستیار سراسری Gemma.
      const res = await sendOfficeAssistantMessage(apiBase, {
        message,
        document_text: (documentText ?? "").slice(0, 48_000),
        recent,
        mode: "legal",
      });
      const reply = res.reply;

      setMessages((prev) => {
        const next = prev
          .filter((m) => m.id !== loadingId)
          .concat({
            id: mid(),
            role: "assistant",
            content: reply || "پاسخی دریافت نشد.",
            createdAt: new Date().toISOString(),
          });
        persist(next);
        return next;
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "خطای دستیار";
      setError(msg);
      setMessages((prev) => {
        const next = prev
          .filter((m) => m.id !== loadingId)
          .concat({
            id: mid(),
            role: "assistant",
            content: `متأسفانه پاسخ دریافت نشد: ${msg}`,
            createdAt: new Date().toISOString(),
          });
        persist(next);
        return next;
      });
    } finally {
      setBusy(false);
    }
  }

  function onSend() {
    const text = input.trim();
    if (!text || busy) return;
    requireAuth(() => {
      const userMsg: DadnoChatMessage = {
        id: mid(),
        role: "user",
        content: text,
        createdAt: new Date().toISOString(),
      };
      const loadingMsg: DadnoChatMessage = {
        id: mid(),
        role: "assistant",
        content: "در حال اعمال…",
        createdAt: new Date().toISOString(),
        loading: true,
      };
      setInput("");
      setMessages((prev) => {
        const base = prev.filter((m) => !m.loading).concat(userMsg);
        void sendToAssistant(text, base, loadingMsg.id);
        return [...base, loadingMsg];
      });
    });
  }

  const canSend = Boolean(input.trim()) && !busy;

  return (
    <aside className="dadno-chat-page dadno-chat-page--contract" dir="rtl">
      <div className="dadno-chat-toolbar">
        <button
          type="button"
          className="dadno-chat-toolbar-btn"
          onClick={onBack}
          aria-label="بازگشت"
        >
          <ArrowRight size={16} aria-hidden />
        </button>
        <h2 className="dadno-chat-title">{title}</h2>
      </div>

      <div className="dadno-chat-thread" ref={listRef}>
        <div className="dadno-chat-thread-inner">
          {messages.map((m) => {
            if (m.role === "user") {
              return (
                <div key={m.id} className="dadno-msg dadno-msg--user" dir="ltr">
                  <div className="dadno-msg-user-col">
                    <p className="dadno-msg-role" dir="rtl">
                      شما
                    </p>
                    <div className="dadno-msg-user-bubble" dir="rtl">
                      <p className="dadno-msg-text">{m.content}</p>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <div key={m.id} className="dadno-msg dadno-msg--assistant" dir="ltr">
                <div className="dadno-msg-assistant-col" dir="rtl">
                  <div className="dadno-msg-assistant-head">
                    <span className="dadno-msg-wand" aria-hidden>
                      <WandSparkles size={12} />
                    </span>
                    <p className="dadno-msg-role">دستیار</p>
                  </div>
                  <div className="dadno-msg-assistant-body">
                    {m.loading ? (
                      <p className="dadno-msg-busy">
                        <span className="dadno-msg-wand" aria-hidden>
                          <WandSparkles size={12} className="spin" />
                        </span>
                        <span className="dadno-msg-busy-text">{m.content}</span>
                      </p>
                    ) : (
                      <p className="dadno-msg-text">{m.content}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {busy && !messages.some((m) => m.loading) ? (
            <div className="dadno-msg dadno-msg--assistant" dir="ltr">
              <p className="dadno-msg-busy" dir="rtl">
                <span className="dadno-msg-wand" aria-hidden>
                  <WandSparkles size={12} className="spin" />
                </span>
                <span className="dadno-msg-busy-text">در حال اعمال…</span>
              </p>
            </div>
          ) : null}

          <div ref={bottomRef} />
        </div>
      </div>

      {error ? <p className="dadno-chat-error">{error}</p> : null}

      <div className="dadno-chat-composer-shell">
        <form
          className="dadno-chat-composer"
          onSubmit={(e) => {
            e.preventDefault();
            onSend();
          }}
        >
          <div className="dadno-chat-composer-card">
            <textarea
              rows={2}
              dir="rtl"
              value={input}
              disabled={busy}
              onChange={(e) => setInput(e.target.value)}
              placeholder={placeholder}
              aria-label="پیام دستیار"
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
            />
            <div className="dadno-chat-composer-bar">
              <button
                type="submit"
                className="dadno-chat-send"
                disabled={!canSend}
                aria-label={busy ? "در صف بفرست" : "ارسال"}
              >
                {busy ? (
                  <Loader2 size={16} className="spin" aria-hidden />
                ) : (
                  <ArrowUp size={16} aria-hidden />
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </aside>
  );
}
